import { Glue42Core } from "@glue42/core";
import { Glue42 } from "../../glue";

export const InMemoryStoreCommandMethodName = "T42.ACS.InMemoryStoreCommand";

export type InMemoryStoreCommandArgs = InMemoryImportCommand | InMemoryExportCommand | InMemoryRemoveCommand | InMemoryClearCommand;

export interface GenericInMemoryCommand<C, A> {
    command: C;
    args?: A;
}

type InMemoryImportCommand = GenericInMemoryCommand<"import", InMemoryImportArgs>;
interface InMemoryImportArgs {
    apps: Glue42.AppManager.Definition[];
    mode?: "merge" | "replace";
}

type InMemoryRemoveCommand = GenericInMemoryCommand<"remove", { apps: string[] }>;
export interface InMemoryRemoveArgs {
    apps: string[];
}

type InMemoryExportCommand = GenericInMemoryCommand<"export", undefined>;
type InMemoryClearCommand = GenericInMemoryCommand<"clear", undefined>;

export interface InMemoryExportResult {
    apps: Glue42.AppManager.Definition[];
}

export class InMemoryStore implements Glue42.AppManager.InMemoryStore {

    constructor(private interop: Glue42Core.Interop.API) {
    }

    public import(apps: Glue42.AppManager.Definition[], mode?: "replace" | "merge"): Promise<Glue42.AppManager.ImportResult> {
        if (!apps || !Array.isArray(apps)) {
            return Promise.reject("invalid apps argument - should be an array of application definitions");
        }
        if (mode && mode !== "replace" && mode !== "merge") {
            return Promise.reject("invalid mode argument - should be 'replace' or 'merge'");
        }

        mode = mode ?? "replace";
        const command: InMemoryImportCommand = {
            command: "import",
            args: {
                apps,
                mode
            }
        };
        return this.interop.invoke<Glue42.AppManager.ImportResult>(InMemoryStoreCommandMethodName, command)
            .then((r) => r.returned);
    }

    public export(): Promise<Glue42.AppManager.Definition[]> {
        return this.interop.invoke<InMemoryExportResult>(InMemoryStoreCommandMethodName, { command: "export" })
            .then((r) => r.returned.apps);
    }

    public remove(app: string): Promise<void> {
        if (!app || typeof app !== "string") {
            return Promise.reject("invalid app name, should be a string value");
        }
        const command: InMemoryRemoveCommand = {
            command: "remove",
            args: {
                apps: [app]
            }
        };
        return this.interop.invoke<void>(InMemoryStoreCommandMethodName, command).then((r) => r.returned);
    }

    public clear(): Promise<void> {
        const command: InMemoryClearCommand = {
            command: "clear"
        };
        return this.interop.invoke<void>(InMemoryStoreCommandMethodName, command).then((r) => r.returned);
    }

    public createAppDef(name: string, url: string): Glue42.AppManager.Definition {
        if (!url) {
            url = "https://google.com";
        }
        return {
            name,
            type: "window",
            title: name,
            details: {
                url
            }
        };
    }
}
